import { isHex } from 'viem';
import { z } from 'zod';
import { ContractMethod } from '@soundxyz/utils/const';

export const ExtendedContractMethod = {
  ...ContractMethod,
  ENABLE_AFFILIATE_FEE: 'ENABLE_AFFILIATE_FEE',
  DISABLE_AFFILIATE_FEE: 'DISABLE_AFFILIATE_FEE',
  MULTICALL__EDIT: 'MULTICALL__EDIT',
} as const;

export type ExtendedContractMethod =
  (typeof ExtendedContractMethod)[keyof typeof ExtendedContractMethod];

const IndividualTransactionSchema = z.object({
  hash: z.string().refine(isHex),
  from: z.string(),
  chainId: z.number(),
  nonce: z.number(),
  contractMethod: z.nativeEnum(ExtendedContractMethod),
  releaseId: z.string().optional(),
});

export type IndividualTransaction = z.infer<typeof IndividualTransactionSchema>;

export enum IndividualTransactionStatus {
  UNKNOWN = 'UNKNOWN',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}
const InvididualTransactionStatusSchema = z.nativeEnum(IndividualTransactionStatus);

export enum ParentTransactionStatus {
  TOTAL_SUCCESS = 'TOTAL_SUCCESS', // when at least the last dispatched transaction is successful
  PARTIAL_SUCCESS = 'PARTIAL_SUCCESS', // when at least one of the dispatched transactions is successful, while the last one is anything other than successful
  TOTAL_FAILURE = 'TOTAL_FAILURE', // when all the dispatched transactions are failed
  UNKNOWN = 'UNKNOWN', // when there's no partial or total success, but at least the last dispatched transaction is unknown
}

const dateLike = z
  .union([z.string(), z.date()])
  .transform(s => (typeof s === 'string' ? new Date(s) : s));

const TransactionStatusLineItemSchema = z.object({
  transaction: IndividualTransactionSchema, // the trx payload as it directly comes from Ethers.js
  status: InvididualTransactionStatusSchema, // what we know about whether the transaction succeeded.
  createdAt: dateLike, // when the frontend dispatched this transaction.
  lastVerificationAttemptDispatchedAt: dateLike, // when we last tried to understand what happened to this transaction (needed for offline persistence & refresh retries)
  lastVerificationAttemptCompletedAt: dateLike.nullable(), // when we last learned what happened to this transaction.
  numVerificationAttempts: z.number(), // how many times this transaction was attempted to verify
});

export type TransactionStatusLineItem = z.infer<typeof TransactionStatusLineItemSchema>;

const TransactionStatusParentSchema = z.object({
  // if 2 different transactions have the same nonce and signer, they are considered to belong to the same parent.
  // items: z.array(TransactionStatusLineItemSchema), // at least one item (i.e actual transaction)
  items: z.tuple([TransactionStatusLineItemSchema]).rest(TransactionStatusLineItemSchema),
});

export type TransactionStatusParent = z.infer<typeof TransactionStatusParentSchema>;

export const TransactionStatusStoreDataSchema = z.record(TransactionStatusParentSchema);

export type TransactionStatusStoreData = z.infer<typeof TransactionStatusStoreDataSchema>;
